/**
 * TaxMapperSuggestionsModal — Review-and-accept UI for AI mapping suggestions.
 *
 * Same component handles BOTH bulk (Tax Mapper page) and single-account
 * (Tax Mapping Report inline) flows. Caller passes:
 *   - workspaceId, formType
 *   - accountIds (optional) — if absent, server suggests for ALL unmapped
 *   - onApplied(count) — callback fired after mappings are persisted, so the
 *     caller can refresh its own state (mappings table / unmapped list)
 *
 * UX
 *   • Suggestions grouped by confidence (high ≥85, medium 60-84, low <60)
 *   • One-click "Apply all high-confidence" CTA
 *   • Per-row Accept / Edit (line dropdown) / Reject
 *   • Confidence pill colors match Anomaly Hunter / risk surfacing language
 */
import { useEffect, useState } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Button } from '../ui/button';
import { Loader2, Sparkles, Check, X, Edit3, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from './constants';

const CONFIDENCE_BUCKETS = [
  { key: 'high',   label: 'High confidence',   min: 85, badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', dot: 'bg-emerald-500' },
  { key: 'medium', label: 'Medium confidence', min: 60, badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',         dot: 'bg-amber-500' },
  { key: 'low',    label: 'Low confidence',    min: 0,  badge: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',             dot: 'bg-rose-500' },
];

function bucketOf(c) {
  if (c >= 85) return CONFIDENCE_BUCKETS[0];
  if (c >= 60) return CONFIDENCE_BUCKETS[1];
  return CONFIDENCE_BUCKETS[2];
}

export default function TaxMapperSuggestionsModal({
  workspaceId, formType, accountIds, formLines,
  onClose, onApplied,
}) {
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [decisions, setDecisions] = useState({}); // account_id -> {action: 'accept'|'reject'|'edit', line: ''}
  const [model, setModel] = useState('');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);

  // Flatten the form's lines so the per-row "Edit" dropdown can offer all options
  const allLines = (formLines?.sections || []).flatMap(s =>
    (s.lines || []).map(l => ({ ...l, section: s.name }))
  );

  // Fetch suggestions on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-mapper/ai-suggest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ form_type: formType, account_ids: accountIds || null }),
        });
        const j = await r.json();
        if (cancelled) return;
        if (!r.ok) {
          setError(j.detail || 'Failed to get suggestions');
          return;
        }
        setSuggestions(j.suggestions || []);
        setModel(j.model || '');
        // Default each suggestion to "accept" — user can toggle off
        const initial = {};
        for (const s of (j.suggestions || [])) {
          initial[s.account_id] = { action: 'accept', line: s.suggested_line };
        }
        setDecisions(initial);
      } catch {
        if (!cancelled) setError('Network error — please try again');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId, formType, accountIds]);

  // Counters for the action bar
  const acceptedCount = Object.values(decisions).filter(d => d.action === 'accept').length;

  const applyHighConfidence = () => {
    const next = { ...decisions };
    for (const s of suggestions) {
      if (s.confidence >= 85) {
        next[s.account_id] = { action: 'accept', line: s.suggested_line };
      }
    }
    setDecisions(next);
  };

  const handleApply = async () => {
    const acceptedEntries = suggestions
      .filter(s => decisions[s.account_id]?.action === 'accept')
      .map(s => ({
        account_id: s.account_id,
        line: decisions[s.account_id].line || s.suggested_line,
      }));
    if (acceptedEntries.length === 0) {
      toast.error('No mappings selected to apply');
      return;
    }
    setApplying(true);
    try {
      // Pull existing mappings so we MERGE rather than overwrite
      const existingRes = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-mapper`);
      const existing = (await existingRes.json()).mappings || [];
      const merged = new Map();
      for (const m of existing) {
        if (m.form_type === formType) merged.set(m.account_id, { account_id: m.account_id, line: m.line || m.tax_line });
      }
      for (const e of acceptedEntries) merged.set(e.account_id, e);

      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-mapper`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_type: formType, mappings: Array.from(merged.values()) }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || 'Failed to save mappings');
        return;
      }
      toast.success(`${acceptedEntries.length} mapping${acceptedEntries.length === 1 ? '' : 's'} applied`);
      onApplied && onApplied(acceptedEntries.length);
      onClose && onClose();
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="tax-mapper-suggestions-modal">
      <div className="bg-background rounded-2xl shadow-2xl max-w-3xl w-full max-h-[88vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-950/30 dark:to-blue-950/30 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-sm">AI Mapping Suggestions</h2>
              <p className="text-[11px] text-muted-foreground">
                {model ? `Powered by ${model}` : 'Reviewing your unmapped accounts'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-accent rounded-md" data-testid="suggestions-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-12 flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
              <p className="text-sm text-muted-foreground">Analyzing your accounts… this can take 5-10s</p>
            </div>
          )}
          {!loading && error && (
            <div className="p-10 text-center">
              <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
              <p className="text-sm text-foreground font-medium">{error}</p>
            </div>
          )}
          {!loading && !error && suggestions.length === 0 && (
            <div className="p-10 text-center">
              <Check className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm font-medium">Nothing to suggest — all your active accounts are already mapped.</p>
            </div>
          )}
          {!loading && !error && suggestions.length > 0 && (
            <div className="p-4 space-y-5">
              {CONFIDENCE_BUCKETS.map((bucket) => {
                const items = suggestions.filter((s) => bucketOf(s.confidence).key === bucket.key);
                if (items.length === 0) return null;
                return (
                  <div key={bucket.key}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-2 h-2 rounded-full ${bucket.dot}`} />
                      <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        {bucket.label} <span className="font-normal">· {items.length}</span>
                      </h3>
                    </div>
                    <ul className="space-y-2">
                      {items.map((s) => {
                        const dec = decisions[s.account_id] || { action: 'reject', line: s.suggested_line };
                        const isAccepted = dec.action === 'accept';
                        return (
                          <li
                            key={s.account_id}
                            className={`rounded-lg border p-3 transition-colors ${isAccepted ? 'bg-emerald-50/40 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800' : 'bg-card'}`}
                            data-testid={`suggestion-row-${s.account_id}`}
                          >
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium text-sm truncate">
                                    {s.account_code ? `${s.account_code} · ` : ''}{s.account_name}
                                  </p>
                                  <span className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground">{s.account_type}</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5 italic">"{s.reasoning}"</p>
                                <div className="mt-2 flex items-center gap-2 flex-wrap">
                                  <span className="text-xs text-muted-foreground">→</span>
                                  <select
                                    value={dec.line}
                                    onChange={(e) => setDecisions({ ...decisions, [s.account_id]: { ...dec, line: e.target.value, action: 'accept' } })}
                                    className="h-7 px-2 text-xs rounded-md border bg-background font-mono"
                                    data-testid={`suggestion-line-select-${s.account_id}`}
                                  >
                                    {allLines.map((l) => (
                                      <option key={l.line} value={l.line}>
                                        {l.line} — {l.description}
                                      </option>
                                    ))}
                                  </select>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${bucket.badge}`}>
                                    {s.confidence}%
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  size="sm"
                                  variant={isAccepted ? 'default' : 'outline'}
                                  onClick={() => setDecisions({ ...decisions, [s.account_id]: { ...dec, action: 'accept' } })}
                                  className={isAccepted ? 'bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2' : 'h-7 px-2'}
                                  data-testid={`suggestion-accept-${s.account_id}`}
                                >
                                  <Check className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant={!isAccepted ? 'default' : 'outline'}
                                  onClick={() => setDecisions({ ...decisions, [s.account_id]: { ...dec, action: 'reject' } })}
                                  className={!isAccepted ? 'bg-zinc-600 hover:bg-zinc-700 text-white h-7 px-2' : 'h-7 px-2'}
                                  data-testid={`suggestion-reject-${s.account_id}`}
                                >
                                  <X className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer / action bar */}
        {!loading && !error && suggestions.length > 0 && (
          <div className="px-5 py-3 border-t bg-muted/30 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Edit3 className="w-3 h-3" />
              {acceptedCount} of {suggestions.length} selected
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={applyHighConfidence} data-testid="suggestions-apply-high-conf">
                Select all high-confidence
              </Button>
              <Button
                size="sm"
                onClick={handleApply}
                disabled={applying || acceptedCount === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="suggestions-apply-btn"
              >
                {applying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
                Apply {acceptedCount} mapping{acceptedCount === 1 ? '' : 's'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
